'use client';

import {
	Dispatch,
	PropsWithChildren,
	createContext,
	useContext,
	useReducer,
} from 'react';
import { produce } from '@/globals/helpers/immer';

export type AccordionItemID = number | string;

export type AccordionItemsState = Map<AccordionItemID, boolean>;

type AccordionState = {
	defaultItemOpenedState: boolean | undefined;
	items: AccordionItemsState;
};

type AccordionContextType = {
	accordionState: AccordionState;
	dispatchOpenedItems: Dispatch<Parameters<typeof accordionStateReducer>[1]>;
	defaultOpened: Set<AccordionItemID> | undefined;
};

const AccordionContext = createContext<AccordionContextType | undefined>(
	undefined,
);

const accordionStateReducer = produce(
	(
		accordionStateDraft: AccordionState,
		action:
			| { type: 'open'; id: AccordionItemID }
			| { type: 'close'; id: AccordionItemID }
			| { type: 'openAll' }
			| { type: 'closeAll' },
	) => {
		switch (action.type) {
			case 'open':
				accordionStateDraft.items.set(action.id, true);
				return;

			case 'close':
				accordionStateDraft.items.set(action.id, false);
				return;

			case 'openAll':
				accordionStateDraft.defaultItemOpenedState = true;
				accordionStateDraft.items.clear();
				return;

			case 'closeAll':
				accordionStateDraft.defaultItemOpenedState = false;
				accordionStateDraft.items.clear();
				return;
		}
	},
);

export const AccordionProvider = ({
	children,
	defaultOpened = new Set<AccordionItemID>(),
}: PropsWithChildren<{ defaultOpened?: Set<AccordionItemID> }>) => {
	const [accordionState, dispatchOpenedItems] = useReducer(
		accordionStateReducer,
		{
			defaultItemOpenedState: undefined,
			items: new Map(),
		},
	);

	return (
		<AccordionContext.Provider
			value={{ accordionState, dispatchOpenedItems, defaultOpened }}
		>
			{children}
		</AccordionContext.Provider>
	);
};

export const useAccordionItemOpened = (id: AccordionItemID) => {
	const context = useContext(AccordionContext);
	if (context == null) {
		throw new Error(
			'useAccordionItemOpened must be used within a AccordionProvider',
		);
	}

	const { accordionState, defaultOpened } = context;

	return (
		accordionState.items.get(id) ??
		accordionState.defaultItemOpenedState ??
		defaultOpened?.has(id) ??
		false
	);
};

export const useAreAllItemsOpened = (itemsCount: number) => {
	const context = useContext(AccordionContext);
	if (context == null) {
		throw new Error(
			'useAccordionOpenedItemsCount must be used within a AccordionProvider',
		);
	}

	const { items, defaultItemOpenedState } = context.accordionState;

	const areAllKnownItemsOpened = [...items.values()].every(Boolean);

	if (defaultItemOpenedState === true) {
		return areAllKnownItemsOpened;
	}

	return items.size === itemsCount && areAllKnownItemsOpened;
};

export const useAreAllItemsClosed = (itemsCount: number) => {
	const context = useContext(AccordionContext);
	if (context == null) {
		throw new Error(
			'useAccordionOpenedItemsCount must be used within a AccordionProvider',
		);
	}

	const { items, defaultItemOpenedState } = context.accordionState;

	const areAllKnownItemsClosed = [...items.values()].every(
		(item) => item === false,
	);

	if (
		defaultItemOpenedState === false ||
		defaultItemOpenedState === undefined
	) {
		return areAllKnownItemsClosed;
	}

	return items.size === itemsCount && areAllKnownItemsClosed;
};

export const useDispatchAccordion = () => {
	const context = useContext(AccordionContext);
	if (context == null) {
		throw new Error(
			'useDispatchAccordion must be used within a AccordionProvider',
		);
	}

	return context.dispatchOpenedItems;
};
